import { useState } from "@nodes/react";
import Box from "@nodes/@material-ui/core/Box";
import Typography from "@nodes/@material-ui/core/Typography";
import Button from "@nodes/@material-ui/core/Button";
import Grid from "@nodes/@material-ui/core/Grid";
import Divider from "@nodes/@material-ui/core/Divider";
import Alert from "@nodes/@material-ui/lab/Alert";
import { FcDebt } from "@nodes/react-icons/fc";
import { UseCustomerBalanceV2 } from "../server/fetcher";
import { getBlockWarning } from "../functions/customerBlock";

const CustomerBalance = ({ history, owner_id, mainOnly = 1 }) => {
  const [show, setShow] = useState(true);
  const { data } = UseCustomerBalanceV2(owner_id, mainOnly);

  const localName = "custBalanceHiddenDay";
  const balanceHiddenDay = localStorage.getItem(localName);

  const d = new Date();
  let day = d.getDay();

  if (!show || `${owner_id}-${day}` === balanceHiddenDay) return null;

  // A strictly blocked account is already looking at the full "Cuenta desabilitada por
  // falta de Pagos" page, which carries its own Desblocquear button. Stacking this banner
  // on top of it says the same thing twice. Temporary unblocks are not affected: those
  // have isDisabled false and keep their own banner.
  if (data?.isDisabled) return null;

  const handleClick = (action) => {
    if (action === "view") {
      history.push(`/customer/balance/${owner_id}/${mainOnly}`);
    } else {
      setShow(false);
      localStorage.setItem(localName, `${owner_id}-${day}`);
    }
  };

  // Wording comes from getBlockWarning() so the cobro and central banners always say the
  // same thing; only the buttons differ per state.
  const getDisplay = (data) => {
    const warning = getBlockWarning(data);

    if (warning) {
      return {
        variant: warning.severity === "error" ? "filled" : "standard",
        severity: warning.severity,
        title: warning.title,
        text: warning.text,
        buttonVariant: "contained",
        // An error-severity banner renders filled red, so it gets the emphasized button:
        // a grey secondary button on a solid red alert reads as a lesser warning than it
        // is. Covers "Desbloqueo temporal" and the pre-rollout countdown; the plain
        // warning keeps its default.
        buttonColor: warning.severity === "error" ? "primary" : undefined,
        secondButtonText: "CERRAR",
      };
    }

    return {
      variant: "standard",
      severity: "warning",
      title: "Balance Pendiente!",
      text: "Usted tiene pago(s) vencido, por favor evite atraso!",
      secondButtonText: "CERRAR",
    };
  };

  const RenderCustomerPaymentReminder = ({ data = {} }) => {
    const display = getDisplay(data);

    return (
      <>
        <Box>
          <Alert icon={<FcDebt size="1.4em" />} variant={display.variant} severity={display.severity}>
            <Typography variant="h5" component="p" gutterBottom>
              {display.title}
            </Typography>

            <Typography variant="h6" component="p" paragraph>
              {display.text}
            </Typography>

            <Divider className="mb-3" />

            <div className="text-right">
              <Grid container>
                <Grid item xs={6}>
                  <Button
                    onClick={() => handleClick("close")}
                    size="small"
                    variant="contained"
                    color={display.buttonColor ?? "secondary"}
                  >
                    {display.secondButtonText}
                  </Button>
                </Grid>
                <Grid item xs={5}>
                  <Button
                    onClick={() => handleClick("view")}
                    size="small"
                    variant={display.buttonVariant ?? "outlined"}
                    color="primary"
                  >
                    Ver Factura
                  </Button>
                </Grid>
              </Grid>
            </div>
          </Alert>
        </Box>
      </>
    );
  };

  // `balance` is absent when the API redacts billing figures for the cobro app
  // (customer_balance.mainOnly = 1), so the block fields have to be able to raise the
  // banner on their own - otherwise those owners would never see the countdown.
  const shouldWarn = +data?.balance > 0 || data?.willBeDisabled;

  return shouldWarn ? <RenderCustomerPaymentReminder data={data} /> : null;
};

export default CustomerBalance;
